import React, { useState, useEffect } from "react";
import "../styles/CompletedProjects.css";
import Footer from "./Footer";
import Navbar from "./NavBar";
import Seo from "./Seo";
import { breadcrumbJsonLd } from "../seoSchema";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import { resolveFirestoreImage, useCmsHeroImageReady } from "../firestoreMedia";
import { uploadCmsImageFile } from "../cmsStorageUpload";

/** Optional caption for gallery lightbox; stored on image objects in Firestore. */
function galleryImageComment(image) {
  if (image == null || typeof image === "string") return "";
  const c = image.comment;
  return typeof c === "string" && c.trim() ? c.trim() : "";
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIRESTORE_API_KEY,
  authDomain: "wishuponaplayhouse.firebaseapp.com",
  projectId: "wishuponaplayhouse",
  storageBucket: "wishuponaplayhouse.firebasestorage.app",
  messagingSenderId: "513377368862",
  appId: "1:513377368862:web:eadda2a0b7bb141ffa670f",
  measurementId: "G-NZ116LS0Q1",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);

const Project = ({
  project,
  isAdmin,
  showEdits,
  onUpdateProject,
  onDeleteProject,
  isUploading,
  setUploadingProject,
  onOpen,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [editedTitle, setEditedTitle] = useState(project.title);
  const [editedDescription, setEditedDescription] = useState(
    project.description || "",
  );

  useEffect(() => {
    setEditedTitle(project.title);
    setEditedDescription(project.description || "");
  }, [project]);

  const nextImage = () => {
    setCurrentImageIndex((currentImageIndex + 1) % project.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (currentImageIndex - 1 + project.images.length) % project.images.length,
    );
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploadingProject(project.id);
      const { url, storagePath } = await uploadCmsImageFile(
        file,
        `cms/gallery/projects/${project.id}`,
      );
      const newImages = [...project.images];
      const prev = newImages[currentImageIndex];
      const keepComment =
        prev &&
        typeof prev === "object" &&
        typeof prev.comment === "string" &&
        prev.comment.trim()
          ? { comment: prev.comment.trim() }
          : {};
      newImages[currentImageIndex] = {
        url,
        storagePath,
        base64: null,
        ...keepComment,
      };
      onUpdateProject(project.id, {
        ...project,
        title: editedTitle,
        description: editedDescription,
        images: newImages,
      });
    } catch (error) {
      console.error("Error uploading image: ", error);
      alert(
        error?.message ||
          "Failed to upload image. Sign in and use the admin gallery editor.",
      );
    } finally {
      setUploadingProject(null);
    }
  };

  const handleAddImage = async () => {
    // Add a new empty image to the project
    const newImages = [
      ...project.images,
      {
        url: `project_${project.id}_new_image_${Date.now()}`,
        base64: null,
      },
    ];

    onUpdateProject(project.id, {
      ...project,
      images: newImages,
    });

    // Switch to the new image
    setCurrentImageIndex(newImages.length - 1);
  };

  const handleRemoveImage = () => {
    if (project.images.length <= 1) {
      alert("Each project must have at least one image.");
      return;
    }

    // Remove the current image
    const newImages = [...project.images];
    newImages.splice(currentImageIndex, 1);

    // Update the project
    onUpdateProject(project.id, {
      ...project,
      images: newImages,
    });

    // Adjust the current index if needed
    if (currentImageIndex >= newImages.length) {
      setCurrentImageIndex(newImages.length - 1);
    }
  };

  const handleSaveChanges = () => {
    onUpdateProject(project.id, {
      ...project,
      title: editedTitle,
      description: editedDescription,
    });
  };

  const getImageSrc = (image) =>
    typeof image === "string"
      ? image
      : resolveFirestoreImage(image, "./images/image-placeholder.png");

  const thumbSrc = getImageSrc(project.images[0]);
  const [thumbLoaded, setThumbLoaded] = useState(false);
  useEffect(() => {
    setThumbLoaded(false);
  }, [thumbSrc, project.id]);

  if (isAdmin && showEdits) {
    return (
      <div className="project project-edit-mode">
        <div className="image-container">
          <img
            src={getImageSrc(project.images[currentImageIndex])}
            alt={project.title}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "./images/image-placeholder.png";
            }}
          />
          <div className="slideshowBtn">
            <button onClick={prevImage}>&lt;</button>
            <button onClick={nextImage}>&gt;</button>
          </div>
        </div>
        <div className="project-edit-container">
          <div className="project-image-controls">
            <div className="image-counter">
              Image {currentImageIndex + 1} of {project.images.length}
            </div>
            <div className="image-upload-container">
              <label>Replace Current Image:</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="file-input"
              />
              {isUploading && project.id === isUploading && (
                <div className="upload-status">Processing...</div>
              )}
            </div>
            <div className="image-action-buttons">
              <button onClick={handleAddImage} className="add-image-btn">
                Add Image
              </button>
              <button onClick={handleRemoveImage} className="remove-image-btn">
                Remove Image
              </button>
            </div>
          </div>
          <div className="project-text-edit">
            <label>Project Title:</label>
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              className="title-input"
            />
            <label>Project Description:</label>
            <textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              className="description-input"
            />
          </div>
          <div className="project-actions">
            <button onClick={handleSaveChanges} className="save-project-btn">
              Save
            </button>
            <button
              onClick={() => onDeleteProject(project.id)}
              className="delete-project-btn"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-grid-item" onClick={onOpen}>
      <img
        className={`gallery-thumb-img cms-media-img${thumbLoaded ? " cms-media-img--loaded" : ""}`}
        src={thumbSrc}
        alt={`${project.title} — custom children's playhouse built by Wish Upon A Playhouse in Denver, Colorado`}
        onLoad={() => setThumbLoaded(true)}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "./images/image-placeholder.png";
        }}
      />
      <div className="gallery-item-label">
        <span>{project.title}</span>
      </div>
    </div>
  );
};

const CompletedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [heroImage, setHeroImage] = useState(null);
  const [isUploading, setIsUploading] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { projectIndex, imageIndex }

  const getImageSrcStatic = (image) =>
    typeof image === "string"
      ? image
      : resolveFirestoreImage(image, "./images/image-placeholder.png");

  const openLightbox = (projectIndex) =>
    setLightbox({ projectIndex, imageIndex: 0 });
  const closeLightbox = () => setLightbox(null);
  const lbNext = () => {
    const proj = projects[lightbox.projectIndex];
    setLightbox({
      ...lightbox,
      imageIndex: (lightbox.imageIndex + 1) % proj.images.length,
    });
  };
  const lbPrev = () => {
    const proj = projects[lightbox.projectIndex];
    setLightbox({
      ...lightbox,
      imageIndex:
        (lightbox.imageIndex - 1 + proj.images.length) % proj.images.length,
    });
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const docRef = doc(firestore, "Pages", "CompletedProjects");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && docSnap.data().projects) {
          const d = docSnap.data();
          setProjects(d.projects);
          if (d.heroImage) setHeroImage(d.heroImage);
        } else {
          // If no projects exist in Firestore, use the default ones
          const defaultProjects = [
            {
              id: 1,
              title: "Woodland Learning Garden Greenhouse",
              description:
                "A custom cottage design for children to learn about gardening.",
              images: ["./images/cottage.png"],
            },
            {
              id: 2,
              title: "Custom Cottage - Henderson",
              description:
                "A beautiful cottage playhouse for a family in Henderson.",
              images: ["./images/ranch.png"],
            },
            // Add more default projects here
          ];
          setProjects(defaultProjects);

          // Save the defaults to Firestore using setDoc instead of updateDoc
          await setDoc(docRef, { projects: defaultProjects });
        }
      } catch (error) {
        console.error("Error fetching projects: ", error);
        // Fallback to default projects if there's an error
        setProjects([
          {
            id: 1,
            title: "Woodland Learning Garden Greenhouse",
            description:
              "A custom cottage design for children to learn about gardening.",
            images: ["./images/cottage.png"],
          },
          {
            id: 2,
            title: "Custom Cottage - Henderson",
            description:
              "A beautiful cottage playhouse for a family in Henderson.",
            images: ["./images/ranch.png"],
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const galStripUrl = resolveFirestoreImage(heroImage, "/images/hero.png");
  const galStripReady = useCmsHeroImageReady(galStripUrl, !isLoading);

  const handleUpdateProject = async (projectId, updatedProject) => {
    try {
      // Find the project index
      const projectIndex = projects.findIndex((p) => p.id === projectId);
      if (projectIndex === -1) return;

      // Create a new array with the updated project
      const updatedProjects = [...projects];
      updatedProjects[projectIndex] = updatedProject;

      // Update local state
      setProjects(updatedProjects);

      // Update Firestore - use setDoc to handle the case where document doesn't exist
      const docRef = doc(firestore, "Pages", "CompletedProjects");
      await setDoc(docRef, {
        projects: updatedProjects,
      });

      console.log("Project updated successfully");
    } catch (error) {
      console.error("Error updating project: ", error);
      alert(
        "Failed to update project. The data might be too large for Firestore.",
      );
    }
  };

  const handleDeleteProject = async (projectId) => {
    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this project?")) {
      return;
    }

    try {
      // Filter out the project to delete
      const updatedProjects = projects.filter((p) => p.id !== projectId);

      // Update local state
      setProjects(updatedProjects);

      // Update Firestore - use setDoc to handle the case where document doesn't exist
      const docRef = doc(firestore, "Pages", "CompletedProjects");
      await setDoc(docRef, {
        projects: updatedProjects,
      });

      console.log("Project deleted successfully");
    } catch (error) {
      console.error("Error deleting project: ", error);
      alert("Failed to delete project");
    }
  };

  if (isLoading) {
    return (
      <div className="project-gallery">
        <Navbar />
        <div className="loading">Loading...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="project-gallery">
      <Seo
        title="Playhouse Gallery — Completed Projects in Denver & Colorado"
        description="Browse our gallery of completed custom children's playhouses built in Denver and across Colorado. Real projects for real families — Ranch, Cottage, Clubhouse, and custom designs."
        path="/gallery"
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Gallery", path: "/gallery" },
        ])}
        jsonLdId="gallery-breadcrumb"
      />
      <Navbar />

      {/* Hero */}
      <div className="sub-hero">
        <div
          className="sub-hero-bg"
          style={{
            backgroundImage: `url('${galStripUrl}')`,
            opacity: galStripReady ? 1 : 0,
            transition: "opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
        <div className="sub-hero-overlay" />
        <div className="sub-hero-content">
          <p className="sub-hero-eyebrow">Our Work</p>
          <h1 className="sub-hero-title">Gallery</h1>
        </div>
      </div>

      <div className="projects-grid-wrapper">
        <div className="gallery-grid">
          {projects.map((project, idx) => (
            <Project
              key={project.id}
              project={project}
              isAdmin={false}
              showEdits={false}
              onUpdateProject={handleUpdateProject}
              onDeleteProject={handleDeleteProject}
              isUploading={isUploading}
              setUploadingProject={setIsUploading}
              onOpen={() => openLightbox(idx)}
            />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox &&
        (() => {
          const proj = projects[lightbox.projectIndex];
          const currentSlide = proj.images[lightbox.imageIndex];
          const imgSrc = getImageSrcStatic(currentSlide);
          const slideComment = galleryImageComment(currentSlide);
          return (
            <div className="lightbox-overlay" onClick={closeLightbox}>
              <div
                className="lightbox-inner"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="lb-close"
                  onClick={closeLightbox}
                  aria-label="Close"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M1 1L13 13M13 1L1 13"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
                <button
                  className="lb-arrow lb-prev"
                  onClick={lbPrev}
                  aria-label="Previous"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M10 3L5 8L10 13"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <img
                  src={imgSrc}
                  alt={`${proj.title} custom playhouse — Wish Upon A Playhouse project in Denver, Colorado`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "./images/image-placeholder.png";
                  }}
                />
                <button
                  className="lb-arrow lb-next"
                  onClick={lbNext}
                  aria-label="Next"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M6 3L11 8L6 13"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div className="lb-info">
                  <span className="lb-title">{proj.title}</span>
                  <div className="lb-info-right">
                    <span className="lb-counter">
                      {lightbox.imageIndex + 1} / {proj.images.length}
                    </span>
                    {slideComment ? (
                      <p className="lb-slide-comment">{slideComment}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      <Footer />
    </div>
  );
};

export default CompletedProjects;
